/**
 * Semantic Colors
 * 
 * Universal semantic colors not in the tenant system.
 * For tenant-specific colors, use the tenant theme.
 * 
 * @see https://www.figma.com/design/LYeg7o8lYMFZD4oPJnGTwn/Design-System-2026
 */
export const semanticColors = {
  /** Success/positive actions (e.g., Open House badge) */
  success: '#32984E',
  /** White */
  white: '#FFFFFF',
  /** Black */
  black: '#000000',
  /** Dark navy - primary text/borders */
  darkNavy: '#191b1e',
  /** Overlay backgrounds */
  overlay: 'rgba(0, 0, 0, 0.4)',
  /** Light gray backgrounds */
  lightGray: '#F5F5F5',
  /** Card background */
  cardBg: '#f9f9f9',
  /** Recency badge background */
  recencyBg: '#F3F6FA',
  /** Recency badge border */
  recencyBorder: '#E7E7E9',
  /** Pagination dots - both active and inactive use white with different opacity */
  pagination: '#FFFFFF',
} as const;

export type SemanticColors = typeof semanticColors;

/**
 * Color Scale Interface
 * Standard structure for color scales with opacity variants from Figma 2026
 */
export interface ColorScale {
  900: string; // 100%
  800: string; // 90%
  700: string; // 80%
  600: string; // 70%
  500: string; // 60%
  400: string; // 40%
  300: string; // 30%
  200: string; // 20%
  100: string; // 10%
  50: string;  // 5%
  25: string;  // 3%
  0?: string;  // 0% (only for primary)
}

/**
 * Partial Color Scale Interface
 * For extension colors with fewer shades
 */
export interface PartialColorScale {
  900?: string;
  800?: string;
  700?: string;
  600?: string;
  500?: string;
  400?: string;
  300?: string;
  200?: string;
  100?: string;
  50?: string;
  25?: string;
}

/**
 * Extended Color Scales (Figma Design System 2026)
 * 
 * TODO: Extract actual color values from Figma using MCP tool
 * These are placeholder values until Figma variables are extracted
 */
export const colorScales = {
  /** Primary brand color scale (12 shades) */
  primary: {
    900: 'var(--color-primary-900, #000000)',
    800: 'var(--color-primary-800, #1a1a1a)',
    700: 'var(--color-primary-700, #333333)',
    600: 'var(--color-primary-600, #4d4d4d)',
    500: 'var(--color-primary-500, #666666)',
    400: 'var(--color-primary-400, #999999)',
    300: 'var(--color-primary-300, #b3b3b3)',
    200: 'var(--color-primary-200, #cccccc)',
    100: 'var(--color-primary-100, #e6e6e6)',
    50: 'var(--color-primary-50, #f2f2f2)',
    25: 'var(--color-primary-25, #f9f9f9)',
    0: 'var(--color-primary-0, #ffffff)',
  } as ColorScale,

  /** Secondary brand color scale (11 shades) */
  secondary: {
    900: 'var(--color-secondary-900, #000000)',
    800: 'var(--color-secondary-800, #1a1a1a)',
    700: 'var(--color-secondary-700, #333333)',
    600: 'var(--color-secondary-600, #4d4d4d)',
    500: 'var(--color-secondary-500, #666666)',
    400: 'var(--color-secondary-400, #999999)',
    300: 'var(--color-secondary-300, #b3b3b3)',
    200: 'var(--color-secondary-200, #cccccc)',
    100: 'var(--color-secondary-100, #e6e6e6)',
    50: 'var(--color-secondary-50, #f2f2f2)',
    25: 'var(--color-secondary-25, #f9f9f9)',
  } as ColorScale,

  /** Burgundy - Rich red tone (6 shades) */
  burgundy: {
    900: 'var(--color-burgundy-900, #4a0000)',
    700: 'var(--color-burgundy-700, #6d0000)',
    500: 'var(--color-burgundy-500, #900000)',
    400: 'var(--color-burgundy-400, #b33333)',
    200: 'var(--color-burgundy-200, #d99999)',
    100: 'var(--color-burgundy-100, #f2cccc)',
  } as PartialColorScale,

  /** Azure - Blue tone (11 shades) */
  azure: {
    900: 'var(--color-azure-900, #001a33)',
    800: 'var(--color-azure-800, #002952)',
    700: 'var(--color-azure-700, #003d70)',
    600: 'var(--color-azure-600, #00528f)',
    500: 'var(--color-azure-500, #0066ad)',
    400: 'var(--color-azure-400, #3385c1)',
    300: 'var(--color-azure-300, #66a3d6)',
    200: 'var(--color-azure-200, #99c2ea)',
    100: 'var(--color-azure-100, #cce0f5)',
    50: 'var(--color-azure-50, #e6f0fa)',
    25: 'var(--color-azure-25, #f2f7fc)',
  } as ColorScale,

  /** Persimmon - Orange tone (6 shades) */
  persimmon: {
    900: 'var(--color-persimmon-900, #4d1f00)',
    700: 'var(--color-persimmon-700, #732e00)',
    500: 'var(--color-persimmon-500, #993d00)',
    400: 'var(--color-persimmon-400, #b36433)',
    200: 'var(--color-persimmon-200, #d99e80)',
    100: 'var(--color-persimmon-100, #f2d9cc)',
  } as PartialColorScale,

  /** Gold - Yellow tone (6 shades) */
  gold: {
    900: 'var(--color-gold-900, #4d4000)',
    700: 'var(--color-gold-700, #736000)',
    500: 'var(--color-gold-500, #998000)',
    400: 'var(--color-gold-400, #b39933)',
    200: 'var(--color-gold-200, #d9c680)',
    100: 'var(--color-gold-100, #f2ebcc)',
  } as PartialColorScale,

  /**
   * Primary Nav - Dark navy scale used for navigation and primary text
   * Maps to existing $color-dark-navy SCSS variables
   */
  primaryNav: {
    900: '#0c0f24', // $color-dark-navy
    800: '#3d3f50', // $color-dark-navy-80
    700: '#9E9FA7', // $color-dark-navy-40
    600: '#cecfd3', // $color-dark-navy-20
    500: '#6d6f7c', // $color-dark-navy-light
    100: '#e8edf5', // $color-dark-navy-10
    50:  '#f3f4f4', // $color-dark-navy-bg
  } as PartialColorScale,
} as const;

/**
 * Overlay Colors
 * Transparent colors for overlays, modals, tooltips
 */
export const overlayColors = {
  /** White overlays - for dark backgrounds */
  white: {
    800: 'rgba(255, 255, 255, 0.8)',
    600: 'rgba(255, 255, 255, 0.6)',
    500: 'rgba(255, 255, 255, 0.4)',
    200: 'rgba(255, 255, 255, 0.2)',
    100: 'rgba(255, 255, 255, 0.1)',
  },
  /** Black overlays - for light backgrounds */
  black: {
    800: 'rgba(0, 0, 0, 0.8)',
    600: 'rgba(0, 0, 0, 0.6)',
    400: 'rgba(0, 0, 0, 0.4)',
    200: 'rgba(0, 0, 0, 0.2)',
    100: 'rgba(0, 0, 0, 0.1)',
  },
} as const;

export type ColorScales = typeof colorScales;
export type OverlayColors = typeof overlayColors;
